import { useTranslation } from 'react-i18next';

type UploadBannerState = 'loading' | 'success' | 'error';

interface DocumentUploadStatusBannerProps {
  state: UploadBannerState;
  onRetry?: () => void;
}

/**
 * @component DocumentUploadStatusBanner
 * @description Renders the current upload state for a document file flow and exposes a retry action on failure.
 *
 * @props
 *   state ('loading' | 'success' | 'error', required) - Controls the banner styling and message set.
 *   onRetry (() => void, optional) - Called when the retry action is pressed in the error state.
 *
 * @notes
 *   Retry is only rendered when the banner is in the error state and a callback is provided.
 *
 * @changelog
 *   2026-03-24 - Initial creation for shared document file create/edit flows.
 */
export function DocumentUploadStatusBanner({
  state,
  onRetry,
}: DocumentUploadStatusBannerProps) {
  const { t } = useTranslation();

  return (
    <div
      className={[
        'rounded-lg border p-4 text-sm',
        state === 'loading'
          ? 'border-primary/30 bg-primary/10 text-primary'
          : state === 'error'
            ? 'border-error/30 bg-error/10 text-error'
            : 'border-confirm/30 bg-confirm/10 text-confirm',
      ].join(' ')}
    >
      <p className="font-medium">
        {state === 'loading'
          ? t('documents.createFile.status.uploadingTitle')
          : state === 'error'
            ? t('documents.createFile.status.errorTitle')
            : t('documents.createFile.status.successTitle')}
      </p>
      <p className="mt-1">
        {state === 'loading'
          ? t('documents.createFile.status.uploadingDescription')
          : state === 'error'
            ? t('documents.createFile.status.errorDescription')
            : t('documents.createFile.status.successDescription')}
      </p>
      {state === 'error' && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-sm font-medium underline underline-offset-2"
        >
          {t('documents.createFile.status.retryAction')}
        </button>
      ) : null}
    </div>
  );
}
